import React, { useContext, useEffect, useState } from 'react'

import { PAGINATION_ICON_URL, DOWNLOAD_ICON, FULLSCREEN_ICON } from '../../../constants/urls'
import { DataContext } from '../../../context/DataContext'
import { downloadEvidence } from '../../../utils/download'

import ShowFullImage from './ShowFullImage'

interface DeliveryEvidenceProps {
  onOpen?: () => void
}

const DeliveryEvidence: React.FC<DeliveryEvidenceProps> = ({ onOpen }: DeliveryEvidenceProps) => {
  const { sale, setOpenAuthStep, loggedIn } = useContext(DataContext)

  const [isOpen, setIsOpen] = useState(false)
  const [evidence, setEvidence] = useState<number>(0)
  const [index, setIndex] = useState<number>(0)
  const [showFullscreen, setShowFullscreen] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    const length = sale.evidencePhotos?.courier?.length
    if (!length || length === 0) return
    setEvidence(length)
  }, [index, sale.evidencePhotos?.courier?.length])

  const handleClick = () => {
    if (onOpen) {
      onOpen()
    }

    if (!loggedIn) {
      setOpenAuthStep(1)
      setIsOpen(false)
    } else {
      setIsOpen(!isOpen)
    }
  }

  const handleDownload = async () => {
    if (isDownloading) return
    try {
      setIsDownloading(true)
      await downloadEvidence(sale.evidencePhotos?.courier || [])
    } finally {
      setIsDownloading(false)
    }
  }

  const handleFullscreen = () => {
    setShowFullscreen(true)
  }

  const handleNavigateToChilexpress = () => {
    window.open(sale?.evidencePhotos?.courier[index], '_blank')
  }

  if (!evidence && (!sale?.verification || sale?.verification.length === 0)) return (<div className='w-full mt-4' />)
  if (sale?.courier?.name === 'chilexpress' && !(sale?.evidencePhotos?.courier?.length > 0)) return (<div className='w-full mt-4' />)

  return (
    <div className='w-full bg-white pt-4 rounded-xl'>
      {sale?.courier?.name !== 'chilexpress' && <button className='flex portrait:w-[calc(100%+2rem)] landscape:w-full items-center pt-2 justify-between gap-2 mb-1 portrait:border-t border-gray-300 portrait:-mx-4 portrait:px-4' type='button' onClick={handleClick}>
        <span className='text-base font-semibold font-[Onest] pt-2'>Evidencia de entrega</span>
        <img
          src={`${PAGINATION_ICON_URL}/icon-next.svg`}
          alt='arrow-right'
          className={`w-6 pt-2 transition-transform duration-300 ${isOpen ? 'rotate-90' : 'rotate-0'}`}
        />
      </button>}

      {sale?.courier?.name === 'chilexpress' && (
        <div className='flex portrait:w-[calc(100%+2rem)] landscape:w-full items-center pt-2 justify-between gap-2 mb-1 portrait:border-t border-gray-300 portrait:-mx-4 portrait:px-4'>
          <span className='text-base font-semibold font-[Onest] pt-2'>Evidencia de entrega</span>
          {loggedIn && <button type='button' onClick={handleNavigateToChilexpress} className='text-sm underline font-[Geist]'>
            Ir a evidencia
          </button>}
        </div>
      )}

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className={`overflow-hidden transition-transform duration-300 ${
          isOpen ? 'translate-y-0' : '-translate-y-2'
        }`}>
          <div className={`${sale.verification && sale.verification.length > 0 ? 'landscape:grid landscape:grid-cols-[2fr_3fr] portrait:flex portrait:flex-col' : 'flex justify-center items-center'} gap-4 font-[Geist]`}>
            {sale.verification && sale.verification.length > 0 && (
              <div className='w-full h-full bg-[#F7F7F7] rounded-xl flex flex-col py-4 px-2 gap-2 text-base'>
                <p className='font-medium'>Pedido recibido por:</p>
                <p className='text-gray-500'>{sale.verification[0].nombre}</p>
                <p className='text-gray-500'>{sale.verification[0].rut}</p>
              </div>
            )}

            {sale.evidencePhotos?.courier?.length > 0 && (
              <div className={`${sale.verification && sale.verification.length > 0 ? 'w-full' : 'w-4/5'}`}>
                <div className='h-full bg-[#F7F7F7] rounded-xl flex p-2'>
                  <button
                    type='button'
                    className='flex flex-shrink-0 items-center p-2 relative'
                    onClick={handleFullscreen} >
                    <img src={sale.evidencePhotos?.courier[index] || ''} alt='evidence' className='h-24 w-24 aspect-square object-cover rounded-lg' />

                    <div className='bg-black/40 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-lg w-12 h-8'>
                      <img src={FULLSCREEN_ICON} alt='fullscreen' />
                    </div>
                  </button>

                  <div className='flex flex-col justify-between w-full my-1 ml-2'>
                    <div className='text-gray-500 text-base'>
                      <h3>{sale.evidencePhotos.courier?.length} Archivos.</h3>
                    </div>

                    <button
                      type='button'
                      onClick={handleDownload}
                      disabled={isDownloading}
                      className={`flex gap-2 items-center h-8 justify-center text-sm bg-gray-200 rounded-lg p-2 w-full ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      <img src={DOWNLOAD_ICON} alt='download' />
                      <p className='text-xs font-medium'>Descargar ({sale.evidencePhotos.courier?.length})</p>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showFullscreen && (
        <ShowFullImage
          isOpen={showFullscreen}
          url={sale.evidencePhotos?.courier[index]}
          index={index}
          setIndex={setIndex}
          total={evidence}
          handleClose={() => setShowFullscreen(false)}
          photos={sale.evidencePhotos?.courier || []}
        />
      )}
    </div>
  )
}

export default DeliveryEvidence
